import React, { FormEvent } from 'react'
import { useForm, Link } from '@inertiajs/react'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControlLabel from '@mui/material/FormControlLabel'
import Checkbox from '@mui/material/Checkbox'
import FormHelperText from '@mui/material/FormHelperText'
import { SidebarLayout } from '@/components/layouts/SidebarLayout'

interface Props {
  minimum_password_length: number
}

export default function New({ minimum_password_length }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    agent: {
      username: '',
      email: '',
      password: '',
      password_confirmation: '',
      agent_type: 'human' as 'human' | 'machine',
      admin: false,
      profile: '',
      agent_provider: 'CLAUDE_CODE',
      agent_arguments: '',
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    post('/manage/agents')
  }

  return (
    <SidebarLayout section="manage" title="New agent">
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 5 },
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          maxWidth: 600,
        }}
      >
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <TextField
            label="Username"
            fullWidth
            autoFocus
            autoComplete="username"
            value={data.agent.username}
            onChange={e => setData('agent', { ...data.agent, username: e.target.value })}
            error={'agent.username' in errors}
            helperText={(errors as Record<string, string>)['agent.username']}
            sx={{ mb: 2.5 }}
          />
          <FormControl fullWidth sx={{ mb: 2.5 }} error={'agent.agent_type' in errors}>
            <InputLabel>Agent type</InputLabel>
            <Select
              value={data.agent.agent_type}
              label="Agent type"
              onChange={e => setData('agent', { ...data.agent, agent_type: e.target.value as 'human' | 'machine' })}
            >
              <MenuItem value="human">Human</MenuItem>
              <MenuItem value="machine">Machine</MenuItem>
            </Select>
            {'agent.agent_type' in errors && (
              <FormHelperText>{(errors as Record<string, string>)['agent.agent_type']}</FormHelperText>
            )}
          </FormControl>
          {data.agent.agent_type === 'human' && (
            <>
              <TextField
                label="Email"
                type="email"
                fullWidth
                autoComplete="email"
                value={data.agent.email}
                onChange={e => setData('agent', { ...data.agent, email: e.target.value })}
                error={'agent.email' in errors}
                helperText={(errors as Record<string, string>)['agent.email']}
                sx={{ mb: 2.5 }}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                autoComplete="new-password"
                value={data.agent.password}
                onChange={e => setData('agent', { ...data.agent, password: e.target.value })}
                error={'agent.password' in errors}
                helperText={(errors as Record<string, string>)['agent.password'] || `Minimum ${minimum_password_length} characters`}
                sx={{ mb: 2.5 }}
              />
              <TextField
                label="Password confirmation"
                type="password"
                fullWidth
                autoComplete="new-password"
                value={data.agent.password_confirmation}
                onChange={e => setData('agent', { ...data.agent, password_confirmation: e.target.value })}
                error={'agent.password_confirmation' in errors}
                helperText={(errors as Record<string, string>)['agent.password_confirmation']}
                sx={{ mb: 2.5 }}
              />
            </>
          )}
          <FormControlLabel
            control={
              <Checkbox
                checked={data.agent.admin}
                onChange={e => setData('agent', { ...data.agent, admin: e.target.checked })}
              />
            }
            label="Admin"
            sx={{ mb: 2.5 }}
          />
          {data.agent.agent_type === 'machine' && (
            <>
              <TextField
                label="Profile"
                fullWidth
                multiline
                minRows={4}
                value={data.agent.profile}
                onChange={e => setData('agent', { ...data.agent, profile: e.target.value })}
                error={'agent.profile' in errors}
                helperText={(errors as Record<string, string>)['agent.profile'] || 'System instructions for this agent (supports Markdown)'}
                sx={{ mb: 2.5 }}
              />
              <FormControl fullWidth sx={{ mb: 2.5 }} error={'agent.agent_provider' in errors}>
                <InputLabel>Provider</InputLabel>
                <Select
                  value={data.agent.agent_provider}
                  label="Provider"
                  onChange={e => setData('agent', { ...data.agent, agent_provider: e.target.value })}
                >
                  <MenuItem value="CLAUDE_CODE">Claude Code</MenuItem>
                </Select>
                {'agent.agent_provider' in errors && (
                  <FormHelperText>{(errors as Record<string, string>)['agent.agent_provider']}</FormHelperText>
                )}
              </FormControl>
              <TextField
                label="Arguments"
                fullWidth
                multiline
                minRows={2}
                value={data.agent.agent_arguments}
                onChange={e => setData('agent', { ...data.agent, agent_arguments: e.target.value })}
                error={'agent.agent_arguments' in errors}
                helperText={(errors as Record<string, string>)['agent.agent_arguments'] || 'JSON array of CLI arguments, e.g. ["--model", "sonnet"]'}
                sx={{ mb: 3 }}
              />
            </>
          )}
          <Button
            type="submit"
            variant="contained"
            fullWidth
            size="large"
            disabled={processing}
            sx={{ mb: 2 }}
          >
            Create agent
          </Button>
          <Box sx={{ textAlign: 'center' }}>
            <Link href="/manage/agents" style={{ color: '#1a1a1a' }}>Back to agents</Link>
          </Box>
        </Box>
      </Paper>
    </SidebarLayout>
  )
}
